import React from 'react';
import { FileItem } from '../FileItem';
import { Tooltip } from '../Tooltip';
import { type ConfirmRemoveText, DropzoneRemoveConfirmSlot } from './DropzoneRemoveConfirm';

export interface InternalFileItemProps {
  name: string;
  extension: string;
  description: React.ReactNode;
  onDownload?: () => void | Promise<void>;
  onRemove?: () => void;
  isProcessing?: boolean;
  progress?: number;
  /** Consumer-set status text from setStatusText() */
  statusText?: string;
  hasError?: boolean;
  disabled?: boolean;
  noConfirm?: boolean;
  confirmText?: ConfirmRemoveText;
  dropzoneRef?: React.RefObject<HTMLElement | null>;
  className?: string;
  text?: {
    removeTooltip?: string;
    downloadTooltip?: string;
    processingText?: string;
  };
}

export function InternalFileItem({
  name,
  extension,
  description,
  onDownload,
  onRemove,
  isProcessing = false,
  progress,
  statusText,
  hasError = false,
  disabled = false,
  noConfirm,
  confirmText,
  dropzoneRef,
  className = 'p-3',
  text,
}: InternalFileItemProps) {
  const [isDownloading, setIsDownloading] = React.useState(false);

  const handleDownload = async () => {
    if (!onDownload || isDownloading) return;
    setIsDownloading(true);
    try {
      await onDownload();
    } finally {
      setIsDownloading(false);
    }
  };

  const loadingText = statusText ?? text?.processingText ?? 'Processing...';
  const isLoading = isProcessing;

  return (
    <FileItem
      fileType={extension}
      indicator="icon"
      variant="row"
      className={className}
      status={hasError ? 'error' : 'default'}
      isLoading={isLoading}
      progress={progress}
      text={{ loading: loadingText }}
    >
      <FileItem.Title>{name}</FileItem.Title>
      <FileItem.Description aria-live="polite">{description}</FileItem.Description>
      <FileItem.Actions className="gap-1">
        {onDownload && (
          <Tooltip mode="label">
            <Tooltip.Trigger>
              <FileItem.DownloadAction
                size="sm"
                onClick={isLoading || hasError ? undefined : handleDownload}
                disabled={isLoading || hasError}
                isLoading={isDownloading}
              />
            </Tooltip.Trigger>
            <Tooltip.Content>
              {text?.downloadTooltip ?? 'Download'}
              <span className="sr-only"> {name}</span>
            </Tooltip.Content>
          </Tooltip>
        )}
        {onRemove && !disabled && (
          <DropzoneRemoveConfirmSlot
            fileName={name}
            onRemove={onRemove}
            disabled={disabled}
            text={confirmText}
            dropzoneRef={dropzoneRef}
            noConfirm={noConfirm}
          />
        )}
      </FileItem.Actions>
    </FileItem>
  );
}
InternalFileItem.displayName = 'Dropzone.InternalFileItem';
